import { useEffect, useMemo, useRef } from 'react'
import { Fancybox } from '@fancyapps/ui'
import parse from 'html-react-parser'
import { FragmentProps } from '../../../interfaces/Publication'
import { usePublication } from '../../../hooks/usePublication'

const IMG = ({ fragment }: FragmentProps) => {
  const { path } = usePublication()
  const imgRef = useRef<HTMLImageElement>(null)

  // Compute image source once
  const imageSrc = useMemo(
    () => `${path}images/${fragment.img}`,
    [path, fragment.img]
  )

  // Parse text once
  const parsedText = useMemo(
    () => (fragment.text ? parse(fragment.text) : null),
    [fragment.text]
  )

  // Bind Fancybox for this component
  useEffect(() => {
    const selector = '[data-fancybox="img-expand"]'

    Fancybox.bind(selector, {
      groupAll: false,
      Thumbs: false,
      defaultType: 'image',
      Carousel: { Navigation: false },
    })

    return () => {
      Fancybox.unbind(selector)
    }
  }, [])

  // Lazy load with Intersection Observer
  useEffect(() => {
    if (!imgRef.current || !('IntersectionObserver' in window)) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && imgRef.current) {
            imgRef.current.src = imageSrc
            observer.unobserve(entry.target)
          }
        })
      },
      {
        rootMargin: '100px',
        threshold: 0.1,
      }
    )

    observer.observe(imgRef.current)

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current)
      }
    }
  }, [imageSrc])

  return (
    <div className="grid grid-2">
      <div className="grid__cell">
        <figure className="image">
          <a href={imageSrc} data-fancybox="img-expand">
            <img
              ref={imgRef}
              className="image__image"
              src="" // lazy load via observer
              data-src={imageSrc}
              alt={fragment.title || ''}
              loading="lazy"
              style={{
                opacity: 0,
                transition: 'opacity 0.3s ease-in',
              }}
              onLoad={(e) => {
                // fade in smoothly
                e.currentTarget.style.opacity = '1'
              }}
            />
          </a>
          {fragment.title && (
            <figcaption className="image__caption">
              {fragment.title}
            </figcaption>
          )}
        </figure>
      </div>
      <div className="grid__cell">{parsedText}</div>
    </div>
  )
}

export default IMG
